// US state list for the Title State control — the SINGLE declaration, shared by
// the client dropdown and the server-side write validation (BL-1449 AC 5).
//
// It lived in `apps/admin-web/src/constants/title.ts` until BL-1449, which made
// `title_state` a server-validated field. A validation rule that is only worth
// enforcing on one side is the drift ADR 016 exists to close, and the server
// could not see an app-local constant — so the data moved here and admin-web's
// `constants/title.ts` re-exports it (same idiom it already uses for
// `TITLE_STATUS_LABELS`).
//
// Scope note: 50 states, no DC and no territories. That is exactly what the
// dropdown has always offered; the server rule is derived FROM this list rather
// than hand-written beside it, so the two can never disagree about the set.

/**
 * `<Select>` options for the Title State control, including its placeholder.
 *
 * Mutable (not `ReadonlyArray`) because `Select`'s `options` prop is
 * `SelectOption[]` — the array was mutable before BL-1449 moved it here, and this
 * move is deliberately behavior-preserving.
 */
export const US_STATES: { value: string; label: string }[] = [
  { value: '', label: 'Select state...' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

/**
 * The valid two-letter codes, derived from {@link US_STATES} so the write-time
 * rule and the dropdown are the same set by construction. Excludes the
 * placeholder — "no state" is expressed as `''`/null at the field level, not as
 * a member of this set.
 */
export const US_STATE_CODES: ReadonlySet<string> = new Set(
  US_STATES.map((s) => s.value).filter((v) => v !== ''),
);
